import axios, { type AxiosError, type AxiosInstance, type AxiosRequestConfig } from 'axios';

import { env } from '@/lib/env';

import { endpoints } from './endpoints';
import { ApiError, UnauthorizedError } from './errors';

type RetryConfig = AxiosRequestConfig & { _retried?: boolean };

export function createApiClient(baseURL: string): AxiosInstance {
  const instance = axios.create({ baseURL, withCredentials: true });
  let refreshInFlight: Promise<void> | null = null;

  const doRefresh = async () => {
    if (!refreshInFlight) {
      refreshInFlight = axios
        .post(`${baseURL}${endpoints.auth.refresh}`, null, { withCredentials: true })
        .then(() => undefined)
        .finally(() => {
          refreshInFlight = null;
        });
    }
    return refreshInFlight;
  };

  instance.interceptors.response.use(
    (r) => r,
    async (error: AxiosError) => {
      const original = error.config as RetryConfig | undefined;
      if (!error.response || !original) {
        return Promise.reject(error);
      }
      const { status, data } = error.response;

      if (status === 401 && !original._retried) {
        original._retried = true;
        try {
          await doRefresh();
        } catch {
          return Promise.reject(new UnauthorizedError('Refresh failed', data));
        }
        return instance.request(original);
      }

      if (status === 401) {
        return Promise.reject(new UnauthorizedError(error.message, data));
      }
      return Promise.reject(new ApiError(error.message, status, data));
    },
  );

  return instance;
}

export const apiClient = createApiClient(env.VITE_API_URL);
